import { notificationHref } from "@/lib/task-notification-link";
import { useCallback, useSyncExternalStore } from "react";
import type { NotificationRecord } from "@/api/notifications";

export type NotificationPermission = "default" | "granted" | "denied";

interface DesktopNotificationState {
  isSupported: boolean;
  permission: NotificationPermission;
}

// ────────────────────────────────────────────────────────────────────────────
// Module-scoped store — NotificationCenter (which requests permission) and
// useNotificationSubscription (which gates on it) each mount their own
// instance of this hook. Plain per-instance useState meant granting
// permission in one instance left every other instance's `permission` stuck
// at "default" until a remount/reload. A shared snapshot + subscriber list
// makes every mounted instance see the update immediately.
// ────────────────────────────────────────────────────────────────────────────

function computeState(): DesktopNotificationState {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return { isSupported: false, permission: "default" };
  }
  return { isSupported: true, permission: Notification.permission };
}

let state: DesktopNotificationState | null = null;
const listeners = new Set<() => void>();

function getSnapshot(): DesktopNotificationState {
  state ??= computeState();
  return state;
}

function setState(next: DesktopNotificationState) {
  state = next;
  listeners.forEach((listener) => listener());
}

/**
 * Recompute from the live `Notification.permission` and publish only if it
 * actually changed — avoids notifying subscribers on every focus/visibility
 * event when nothing moved.
 */
function refreshIfChanged() {
  if (!state) return;
  const next = computeState();
  if (next.isSupported !== state.isSupported || next.permission !== state.permission) {
    setState(next);
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  // A prior hook instance may have left the module-scoped snapshot stale
  // after the user changed this permission outside the app. Sync each fresh
  // subscription, not only focus/visibility events after it mounts.
  refreshIfChanged();

  // The Notification API has no reliable cross-browser permission-change
  // event, so getSnapshot's cached value never re-reads Notification.permission
  // on its own. A user who grants/revokes it via the browser's own site
  // settings (not through requestPermission()) would otherwise leave every
  // mounted instance — e.g. reopening Notification Settings — reporting the
  // stale value until a full reload. Recompute on focus/visibility, the
  // usual proxy for "the user may have changed something outside the tab."
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") refreshIfChanged();
  };
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("focus", refreshIfChanged);

  return () => {
    listeners.delete(listener);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("focus", refreshIfChanged);
  };
}

/**
 * Hook to manage browser desktop notifications
 * Handles permission requests and showing notifications
 */
export function useDesktopNotifications() {
  const { permission, isSupported } = useSyncExternalStore(subscribe, getSnapshot, () => ({
    isSupported: false,
    permission: "default" as const,
  }));

  /**
   * Request permission to show desktop notifications
   */
  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!isSupported) {
      return "denied";
    }

    if (Notification.permission === "granted") {
      refreshIfChanged();
      return "granted";
    }

    if (Notification.permission === "denied") {
      refreshIfChanged();
      return "denied";
    }

    const result = await Notification.requestPermission();
    setState({ isSupported, permission: result });
    return result;
  }, [isSupported]);

  /**
   * Show a desktop notification
   * @param title - Notification title
   * @param options - Notification options (body, icon, tag, etc.)
   */
  const showNotification = useCallback(
    (title: string, options?: NotificationOptions): Notification | null => {
      if (!isSupported || Notification.permission !== "granted") {
        return null;
      }

      const notification = new Notification(title, {
        icon: "/icon-192.png", // App icon
        badge: "/icon-badge.png", // Small badge icon
        requireInteraction: false, // Don't require user interaction
        silent: false, // Play default notification sound
        ...options,
      });

      return notification;
    },
    [isSupported]
  );

  /**
   * Show a desktop notification from a NotificationRecord
   * Formats the notification with proper title, body, and click handling
   */
  const showNotificationFromRecord = useCallback(
    (record: NotificationRecord): Notification | null => {
      if (!isSupported || Notification.permission !== "granted") {
        return null;
      }

      const href = notificationHref(record);
      const actorName = record.actor
        ? `${record.actor.first_name ?? ""} ${record.actor.last_name ?? ""}`.trim()
        : null;

      const body = actorName
        ? `${actorName}: ${record.body || record.title}`
        : record.body || "";

      const notification = showNotification(record.title, {
        body: body.substring(0, 200), // Limit body length
        tag: record.id, // Prevent duplicate notifications
        icon: record.actor?.photo_url || "/icon-192.png",
        data: {
          notificationId: record.id,
          linkUrl: href,
        },
      });

      // Handle notification click - open the link
      if (notification && href) {
        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          window.location.href = href;
          notification.close();
        };
      }

      return notification;
    },
    [isSupported, showNotification]
  );

  return {
    permission,
    isSupported,
    requestPermission,
    showNotification,
    showNotificationFromRecord,
  };
}

/**
 * Hook to automatically show desktop notifications for new in-app notifications
 * Call this once in your app to enable desktop notifications
 */
export function useAutoDesktopNotifications(enabled: boolean = true) {
  const { isSupported, permission } = useDesktopNotifications();

  return {
    isSupported,
    permission,
    enabled: enabled && isSupported && permission === "granted",
  };
}
